import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import {
  PhCaretDown,
  PhCaretUp,
  PhArrowRight,
  PhSparkle,
  PhBookOpen,
  PhLink,
  PhInfo,
} from 'ember-phosphor-icons';

/**
 * Glossary term category for filtering and visual styling
 */
export type GlossaryCategory = 'loans' | 'repayment' | 'forgiveness' | 'financial' | 'general';

/**
 * Glossary term data structure
 */
export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  category: GlossaryCategory;
  relatedTerms?: string[];
  relatedRoute?: string;
  relatedRouteText?: string;
  examples?: string[];
  tip?: string;
}

/**
 * GlossaryTermCard - Interactive glossary term card with expand/collapse
 *
 * Displays a glossary term with definition, category badge, related links,
 * and optional AI explanation integration.
 *
 * @example
 * ```gts
 * <GlossaryTermCard
 *   @term={{this.term}}
 *   @onAskAI={{this.askAboutTerm}}
 * />
 * ```
 */
interface GlossaryTermCardSignature {
  Args: {
    /** The glossary term data */
    term: GlossaryTerm;
    /** Handler for "Ask AI" button click */
    onAskAI?: (term: GlossaryTerm) => void;
    /** Whether to start expanded */
    expanded?: boolean;
    /** Callback when expand state changes */
    onToggle?: (termId: string, expanded: boolean) => void;
  };
  Element: HTMLDivElement;
}

const categoryStyles: Record<GlossaryCategory, { bg: string; text: string; border: string }> = {
  loans: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-l-blue-500',
  },
  repayment: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-l-emerald-500',
  },
  forgiveness: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    text: 'text-violet-700 dark:text-violet-300',
    border: 'border-l-violet-500',
  },
  financial: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-l-amber-500',
  },
  general: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-l-gray-400',
  },
};

const getCategoryLabel = (category: GlossaryCategory): string => {
  const labels: Record<GlossaryCategory, string> = {
    loans: 'Loans',
    repayment: 'Repayment',
    forgiveness: 'Forgiveness',
    financial: 'Financial',
    general: 'General',
  };
  return labels[category];
};

export default class GlossaryTermCard extends Component<GlossaryTermCardSignature> {
  @tracked isExpanded = this.args.expanded ?? false;

  get categoryStyle() {
    return categoryStyles[this.args.term.category];
  }

  get categoryLabel() {
    return getCategoryLabel(this.args.term.category);
  }

  get hasExtendedContent() {
    const term = this.args.term;
    return (
      (term.relatedTerms && term.relatedTerms.length > 0) ||
      term.relatedRoute ||
      (term.examples && term.examples.length > 0) ||
      term.tip
    );
  }

  toggle = () => {
    this.isExpanded = !this.isExpanded;
    this.args.onToggle?.(this.args.term.id, this.isExpanded);
  };

  askAI = () => {
    this.args.onAskAI?.(this.args.term);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all hover:shadow-md border-l-4
        {{this.categoryStyle.border}}"
      ...attributes
    >
      {{! Header - always visible }}
      <button
        type="button"
        class="w-full p-4 flex items-start justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        {{on "click" this.toggle}}
        aria-expanded={{if this.isExpanded "true" "false"}}
      >
        <div class="flex-1 pr-4">
          <div class="flex items-center gap-2 mb-1 flex-wrap">
            <Heading @level={{4}} class="text-gray-900">
              {{@term.term}}
            </Heading>
            <Chip class="text-xs {{this.categoryStyle.bg}} {{this.categoryStyle.text}}">
              {{this.categoryLabel}}
            </Chip>
          </div>
          <Text class="text-gray-600 text-sm {{unless this.isExpanded 'line-clamp-2'}}">
            {{@term.definition}}
          </Text>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0">
          {{#if this.isExpanded}}
            <PhCaretUp @size={{20}} class="text-gray-400" />
          {{else}}
            <PhCaretDown @size={{20}} class="text-gray-400" />
          {{/if}}
        </div>
      </button>

      {{! Expanded content }}
      {{#if this.isExpanded}}
        <div class="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
          {{! Examples }}
          {{#if @term.examples}}
            <div>
              <div class="flex items-center gap-2 mb-2">
                <PhInfo @size={{16}} class="text-blue-500" />
                <Text class="text-sm font-medium text-gray-700">
                  Examples
                </Text>
              </div>
              <ul class="list-disc list-inside space-y-1 text-sm text-gray-600 ml-6">
                {{#each @term.examples as |example|}}
                  <li>{{example}}</li>
                {{/each}}
              </ul>
            </div>
          {{/if}}

          {{! Tip }}
          {{#if @term.tip}}
            <div
              class="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700"
            >
              <div class="flex items-start gap-2">
                <PhSparkle @size={{16}} class="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <Text class="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
                    Pro Tip
                  </Text>
                  <Text class="text-sm text-amber-700 dark:text-amber-400">
                    {{@term.tip}}
                  </Text>
                </div>
              </div>
            </div>
          {{/if}}

          {{! Related Terms }}
          {{#if @term.relatedTerms}}
            <div>
              <div class="flex items-center gap-2 mb-2">
                <PhLink @size={{16}} class="text-violet-500" />
                <Text class="text-sm font-medium text-gray-700">
                  Related Terms
                </Text>
              </div>
              <div class="flex flex-wrap gap-2">
                {{#each @term.relatedTerms as |related|}}
                  <Chip
                    class="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 cursor-pointer hover:bg-violet-200 dark:hover:bg-violet-900/50"
                  >
                    {{related}}
                  </Chip>
                {{/each}}
              </div>
            </div>
          {{/if}}

          {{! Actions }}
          <div class="flex items-center gap-3 pt-2">
            {{#if @term.relatedRoute}}
              <LinkTo
                @route={{@term.relatedRoute}}
                class="inline-flex items-center gap-1 text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium"
              >
                <PhBookOpen @size={{16}} />
                {{#if @term.relatedRouteText}}
                  {{@term.relatedRouteText}}
                {{else}}
                  Learn More
                {{/if}}
                <PhArrowRight @size={{14}} />
              </LinkTo>
            {{/if}}

            {{#if @onAskAI}}
              <Button
                @intent="primary"
                @appearance="minimal"
                class="text-sm"
                {{on "click" this.askAI}}
              >
                <PhSparkle @size={{16}} class="mr-1" />
                Ask AI for More
              </Button>
            {{/if}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
